<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import {
  gaiaAccent,
  gaiaFontSize,
  gaiaFonts,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaPreviewHighlight,
  gaiaShadow,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import {
  IconAdd,
  IconChevronDown,
  IconChevronUp,
  IconClose,
  IconDownload,
  IconRefresh,
  IconRemove,
  IconWarning,
} from '@/components/design/icons';
import { renderAsync } from 'docx-preview';
import {
  RenderService,
  RenderError,
  buildContextData,
} from '@/services/contractGeneration/renderService';
import { collectValidationErrors } from '@/composables/contractGeneration/useExtractedSchema';
import {
  collectBodyParagraphTexts,
  highlightPreviewChanges,
  PREVIEW_CHANGE_CURRENT_CLASS,
} from '@/utils/contractGeneration/previewChanges';
import type { TemplateSchema } from '@/api';

interface Props {
  variantUid: string;
  schema: TemplateSchema;
  formData: Record<string, unknown>;
  suggestedFileName: string;
  disabled?: boolean;
  /**
   * Header title text. Defaults to "Live preview" (matches the wizard's
   * Compose step). Pass `null` to hide the title entirely — e.g. when the
   * panel is embedded in a drawer that already has its own title.
   */
  title?: string | null;
  /**
   * Whether to render the Refresh button. Set to `false` in contexts where
   * the form data is static (e.g. template detail drawer previewing with
   * default values) — refreshing would just re-fetch the same render.
   */
  showRefresh?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  title: 'Live preview',
  showRefresh: true,
});
const emit = defineEmits<{
  (e: 'collapse'): void;
}>();

// Commits are now discrete (fields emit on blur / Enter, not per-keystroke).
// A tiny coalescing window still helps when the user tabs through several
// fields in quick succession — we only want one render for the whole burst.
const DEBOUNCE_MS = 50;

type PreviewStatus = 'idle' | 'loading' | 'ready' | 'needs-fields' | 'error';

const previewContainer = ref<HTMLDivElement | null>(null);
const status = ref<PreviewStatus>('idle');
const errorMessage = ref<string | null>(null);
const lastBlob = ref<Blob | null>(null);

// Zoom: 1.0 = render the document at its true size. Buttons step by 10%.
// A fixed default (e.g. 75%) doesn't work across viewport widths because the
// preview panel's width varies — so we measure the first rendered page and
// compute the scale that makes it fit. User +/- always wins after.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.1;
const zoomLevel = ref<number>(0.75);
// Remember the fitted value so the "click percentage to reset" action returns
// to the initially-fitted scale instead of a hard-coded number.
const fittedZoom = ref<number>(0.75);
const zoomPercent = computed(() => Math.round(zoomLevel.value * 100));

function clampZoom(value: number): number {
  const bounded = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value));
  // Avoid floating-point drift (0.30000000000000004 → 0.3).
  return Math.round(bounded * 10) / 10;
}

function zoomIn(): void {
  zoomLevel.value = clampZoom(zoomLevel.value + ZOOM_STEP);
}

function zoomOut(): void {
  zoomLevel.value = clampZoom(zoomLevel.value - ZOOM_STEP);
}

function resetZoom(): void {
  zoomLevel.value = fittedZoom.value;
}

/**
 * Measure the first rendered page and the scroll container, then compute the
 * scale that makes the page fit the panel width. Returns null when the DOM
 * isn't ready yet.
 */
function computeFitZoom(): number | null {
  const container = previewContainer.value;
  if (!container) return null;

  const firstPage = container.querySelector<HTMLElement>('section');
  if (!firstPage || firstPage.offsetWidth === 0) return null;

  // `zoom` scales both visual and layout, so offsetWidth reflects the scaled
  // width. Divide by current zoom to recover the natural page width.
  const currentZoom = zoomLevel.value || 1;
  const naturalPageWidth = firstPage.offsetWidth / currentZoom;

  const body = container.parentElement;
  if (!body) return null;
  const bodyStyle = window.getComputedStyle(body);
  const padL = parseFloat(bodyStyle.paddingLeft) || 0;
  const padR = parseFloat(bodyStyle.paddingRight) || 0;
  // Reserve a little room for the vertical scrollbar that will appear on
  // multi-page content.
  const scrollbarReserve = 18;
  const availableWidth = body.clientWidth - padL - padR - scrollbarReserve;

  if (naturalPageWidth <= 0 || availableWidth <= 0) return null;

  const scale = availableWidth / naturalPageWidth;
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, scale));
}

// ──────────────────────────────────────────────────────────
// Change tracking — "where did what I just typed land?"
// ──────────────────────────────────────────────────────────
//
// Every committed field re-renders the whole document, and in a long contract
// the passage that picked the value up is anywhere. The panel keeps the
// previous render's paragraphs, diffs the new render against them, tints
// what differs and scrolls the first changed place into view. Several places
// (a party name used throughout) become a pill the user steps through.

/** One scroll target per changed place, in reading order. Plain elements, not reactive. */
const changeTargets = shallowRef<HTMLElement[]>([]);
const activeChangeIndex = ref<number>(0);
/** The user hid the highlights for this render; the next edit shows them again. */
const changesDismissed = ref<boolean>(false);
const changeCount = computed(() => changeTargets.value.length);
const showChangesPill = computed(() => changeCount.value > 0 && !changesDismissed.value);
/**
 * Set when the next render is a different document (a variant switch), so its
 * diff — which would flag every paragraph — is not drawn.
 */
let skipNextDiff = false;

function setChangeTargets(targets: HTMLElement[]): void {
  changeTargets.value = targets;
  activeChangeIndex.value = 0;
  changesDismissed.value = false;
}

/**
 * Centre one changed place in the preview's own scroller. Done by hand rather
 * than `scrollIntoView`, which would also scroll every ancestor and drag the
 * wizard page along with the preview.
 */
function scrollToChange(index: number): void {
  const targets = changeTargets.value;
  if (targets.length === 0) return;
  const bounded = ((index % targets.length) + targets.length) % targets.length;
  for (const target of targets) target.classList.remove(PREVIEW_CHANGE_CURRENT_CLASS);
  const target = targets[bounded];
  target.classList.add(PREVIEW_CHANGE_CURRENT_CLASS);
  activeChangeIndex.value = bounded;

  const scrollParent = previewContainer.value?.parentElement;
  if (!scrollParent) return;
  const parentRect = scrollParent.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top =
    scrollParent.scrollTop +
    (targetRect.top - parentRect.top) -
    (scrollParent.clientHeight - targetRect.height) / 2;
  scrollParent.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

function nextChange(): void {
  scrollToChange(activeChangeIndex.value + 1);
}

function previousChange(): void {
  scrollToChange(activeChangeIndex.value - 1);
}

function dismissChanges(): void {
  changesDismissed.value = true;
}

// Request/session bookkeeping — all in closure state so multiple instances
// of the component stay isolated.
let debounceHandle: number | null = null;
let latestRequestId = 0;
let lastRenderedKey: string | null = null;
let isUnmounted = false;
// Auto-fit runs once on the first successful render so we don't overwrite
// the user's zoom choice on subsequent renders.
let hasAutoFit = false;

// These mutate from outside the render flow (the unmount hook, competing
// renders, Vue clearing the template ref), so read them through function
// calls — TS would otherwise narrow them across `await` boundaries and
// consider the staleness guards impossible.
function componentUnmounted(): boolean {
  return isUnmounted;
}

function renderAborted(requestId: number): boolean {
  return isUnmounted || requestId !== latestRequestId;
}

function currentPreviewContainer(): HTMLDivElement | null {
  return previewContainer.value;
}

const clientSideErrors = computed(() => collectValidationErrors(props.schema, props.formData));

function contextKey(): string {
  return JSON.stringify(buildContextData(props.schema, props.formData));
}

function schedulePreview(): void {
  if (debounceHandle !== null) {
    window.clearTimeout(debounceHandle);
  }
  debounceHandle = window.setTimeout(() => {
    debounceHandle = null;
    void renderPreview();
  }, DEBOUNCE_MS);
}

function cancelDebounce(): void {
  if (debounceHandle !== null) {
    window.clearTimeout(debounceHandle);
    debounceHandle = null;
  }
}

async function renderPreview(options: { force?: boolean } = {}): Promise<void> {
  await nextTick();
  if (isUnmounted || !previewContainer.value) return;

  const key = contextKey();
  if (!options.force && key === lastRenderedKey && status.value === 'ready') {
    return;
  }

  const myRequestId = ++latestRequestId;
  status.value = 'loading';
  errorMessage.value = null;

  try {
    const contextData = JSON.parse(key) as Record<string, unknown>;
    const blob = await RenderService.preview(props.variantUid, contextData);

    if (renderAborted(myRequestId)) return;
    const container = currentPreviewContainer();
    if (!container) return;

    // Preserve the user's reading position across re-renders. Without this,
    // every debounced preview refresh snaps the viewport back to the top
    // mid-edit, which is disruptive when the user is tracking changes
    // several pages deep.
    const scrollParent = container.parentElement;
    const savedScrollTop = scrollParent?.scrollTop ?? 0;
    const savedScrollHeight = scrollParent?.scrollHeight ?? 0;

    // What is on screen right now, for the change diff after the re-render.
    // A manual refresh re-renders the same values and has nothing to show;
    // a new document (variant switch) would differ everywhere.
    const previousTexts =
      options.force || skipNextDiff ? null : collectBodyParagraphTexts(container);
    skipNextDiff = false;

    container.innerHTML = '';
    await renderAsync(blob, container, undefined, {
      // Leave `className` at its default ('docx') so the library emits the
      // predictable class names `.docx-wrapper` on the outer element and
      // `section.docx` on each page — which our scoped CSS targets below.
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      experimental: false,
      trimXmlDeclaration: true,
      useBase64URL: false,
    });

    if (renderAborted(myRequestId)) return;

    // Tint what this render changed and take the user there. An edit whose
    // effect is nowhere on screen is exactly the case the highlight is for.
    const targets =
      previousTexts && previousTexts.length > 0
        ? highlightPreviewChanges(container, previousTexts)
        : [];
    setChangeTargets(targets);

    if (targets.length > 0) {
      await new Promise<void>(resolve => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
      if (componentUnmounted()) return;
      scrollToChange(0);
    } else if (scrollParent && savedScrollTop > 0) {
      // Nothing to point at: restore the reading position after the browser
      // has laid out the new content. Same visual position by ratio, so minor
      // content-size changes don't shift the user's viewport.
      await new Promise<void>(resolve => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
      if (componentUnmounted()) return;
      const newScrollHeight = scrollParent.scrollHeight;
      if (savedScrollHeight > 0 && newScrollHeight > 0) {
        const ratio = savedScrollTop / savedScrollHeight;
        scrollParent.scrollTop = ratio * newScrollHeight;
      } else {
        scrollParent.scrollTop = savedScrollTop;
      }
    }

    lastBlob.value = blob;
    lastRenderedKey = key;
    status.value = 'ready';

    // First render: fit the page to the panel width so there's no horizontal
    // overflow regardless of viewport size. Only runs once — later renders
    // respect whatever zoom the user has chosen.
    if (!hasAutoFit) {
      await new Promise<void>(resolve => {
        window.requestAnimationFrame(() => {
          resolve();
        });
      });
      if (componentUnmounted()) return;
      const scale = computeFitZoom();
      if (scale !== null) {
        zoomLevel.value = scale;
        fittedZoom.value = scale;
      }
      hasAutoFit = true;
    }
  } catch (error) {
    if (renderAborted(myRequestId)) return;

    const message =
      error instanceof RenderError
        ? error.messages.join('\n')
        : error instanceof Error
          ? error.message
          : 'Failed to render preview.';

    // Only the "you haven't filled this field in yet" class of error should
    // hide behind the friendly placeholder while required fields are still
    // empty — e.g. an undefined/missing variable at render time. Everything
    // else (a value the user entered that the backend rejected, like
    // "Cannot format value as cardinal: '1,234'", or a template-structural
    // problem) is actionable right now and is surfaced verbatim.
    const isMissingDataError =
      /undefined|not defined|no attribute|is required|missing (?:required )?(?:field|value|variable)/i.test(
        message
      );

    if (isMissingDataError && clientSideErrors.value.length > 0) {
      status.value = 'needs-fields';
      errorMessage.value = null;
      return;
    }

    status.value = 'error';
    errorMessage.value = message;
  }
}

function handleRefresh(): void {
  cancelDebounce();
  void renderPreview({ force: true });
}

function handleDownload(): void {
  if (!lastBlob.value) return;
  const url = URL.createObjectURL(lastBlob.value);
  const a = document.createElement('a');
  a.href = url;
  a.download = props.suggestedFileName || 'preview.docx';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function handleCollapse(): void {
  emit('collapse');
}

// A different variant is a different document: nothing in the
// previous render corresponds to anything in the next.
watch(
  () => props.variantUid,
  () => {
    skipNextDiff = true;
  }
);

watch(
  () => [props.variantUid, JSON.stringify(props.formData)],
  () => {
    schedulePreview();
  }
);

onMounted(() => {
  // Render once immediately on mount — no debounce — so the user sees
  // something the moment they enter the step. Uses nextTick internally
  // to guarantee the template ref is bound.
  void renderPreview({ force: true });
});

onBeforeUnmount(() => {
  isUnmounted = true;
  cancelDebounce();
  lastBlob.value = null;
});

// --- chrome (tokens for the scoped styles) ---
const fsBody = gaiaFontSize.body;
const fwSemibold = String(gaiaFontWeight.semibold);
const fontMono = gaiaFonts.mono;
const fsMeta = gaiaFontSize.meta;
const fsRow = gaiaFontSize.row;
const gFaint = gaiaNeutral.textFaint;
const gAccentStrong = gaiaAccent.strong;
// Change highlights: a highlighter over the document. The wash tints the
// changed paragraph, the mark the exact characters, and a deeper wash plus a
// one-off flash marks the one the stepper is currently on — no edge bars, the
// paint alone says where the edit landed.
const gChangeWash = gaiaPreviewHighlight.wash;
const gChangeMark = gaiaPreviewHighlight.mark;
const gChangeCurrent = gaiaPreviewHighlight.current;
const gChangeFlash = gaiaPreviewHighlight.flash;
const gRemovedWash = gaiaPreviewHighlight.removed;
const gPillShadow = gaiaShadow.menu;
const gAlertText = gaiaStatus.alertText;
const gAlertTint = gaiaStatus.alertTint;
const gBorder = gaiaNeutral.borderCard;
const gInk = gaiaNeutral.ink;
const gMuted = gaiaNeutral.surfaceMuted;
const gSoft = gaiaNeutral.surfaceSoft;
const gSubtle = gaiaNeutral.textSubtle;
const gSurface = gaiaNeutral.surface;
const gText = gaiaNeutral.text;
</script>

<template>
  <div class="live-preview-panel">
    <n-flex class="preview-header" justify="space-between" align="center" :size="12" :wrap="false">
      <n-flex class="preview-header__title-group" align="center" :size="8" :wrap="false">
        <n-text v-if="title" strong class="preview-header__title">{{ title }}</n-text>
        <n-spin
          v-if="status === 'loading'"
          size="small"
          :stroke-width="22"
          style="width: 14px; height: 14px"
        />
      </n-flex>
      <n-flex class="preview-header__actions" align="center" :size="4" :wrap="false">
        <div class="zoom-control" role="group" aria-label="Zoom">
          <n-button
            size="small"
            quaternary
            :disabled="disabled || zoomLevel <= ZOOM_MIN"
            :aria-label="'Zoom out'"
            @click="zoomOut"
          >
            <template #icon>
              <n-icon><IconRemove /></n-icon>
            </template>
          </n-button>
          <button
            type="button"
            class="zoom-control__label"
            :disabled="disabled"
            :aria-label="'Reset zoom to 100%'"
            @click="resetZoom"
          >
            {{ zoomPercent }}%
          </button>
          <n-button
            size="small"
            quaternary
            :disabled="disabled || zoomLevel >= ZOOM_MAX"
            :aria-label="'Zoom in'"
            @click="zoomIn"
          >
            <template #icon>
              <n-icon><IconAdd /></n-icon>
            </template>
          </n-button>
        </div>
        <span class="preview-header__divider" aria-hidden="true" />
        <n-button
          v-if="showRefresh"
          size="small"
          quaternary
          :disabled="disabled || status === 'loading'"
          @click="handleRefresh"
        >
          <template #icon>
            <n-icon><IconRefresh /></n-icon>
          </template>
          Refresh
        </n-button>
        <n-button size="small" quaternary :disabled="disabled || !lastBlob" @click="handleDownload">
          <template #icon>
            <n-icon><IconDownload /></n-icon>
          </template>
          Download
        </n-button>
        <n-button
          size="small"
          quaternary
          :disabled="disabled"
          :aria-label="'Collapse preview'"
          @click="handleCollapse"
        >
          <template #icon>
            <n-icon><IconClose /></n-icon>
          </template>
        </n-button>
      </n-flex>
    </n-flex>

    <div class="preview-body">
      <!-- Real error (form is client-side valid but backend still failed).
           Usually a template-level problem — stray block tag, unknown filter,
           missing variable, etc. We surface the backend's `detail` verbatim
           so whoever edits the template can act on it directly. -->
      <n-alert
        v-if="status === 'error' && errorMessage"
        type="error"
        :title="'Template render failed'"
        closable
        style="margin-bottom: 12px"
      >
        <p class="preview-error__detail">{{ errorMessage }}</p>
      </n-alert>

      <!-- Placeholder: the form is missing required fields -->
      <n-flex
        v-if="status === 'needs-fields'"
        class="preview-empty"
        justify="center"
        align="center"
      >
        <n-empty :description="'Fill required fields to see the live preview.'">
          <template #icon>
            <n-icon :size="48" :color="gFaint"><IconWarning /></n-icon>
          </template>
          <template #extra>
            <n-text depth="3" class="preview-empty__hint">
              The preview updates as you complete each required field.
            </n-text>
          </template>
        </n-empty>
      </n-flex>

      <!-- Idle placeholder (only before the first render attempt completes) -->
      <n-flex v-else-if="status === 'idle'" class="preview-empty" justify="center" align="center">
        <n-spin size="small" />
      </n-flex>

      <!-- Rendered document container. Kept mounted at all times so docx-preview
           has a stable host. When in 'needs-fields' / 'error' / 'loading' states
           we just hide it behind the overlays above. `zoom` rescales both the
           visual size and the layout box, so the scroll container tracks the
           zoomed dimensions correctly (unlike `transform: scale`). -->
      <div
        ref="previewContainer"
        class="preview-container"
        :class="{
          'preview-container--hidden': status === 'needs-fields',
          'preview-container--changes-hidden': changesDismissed,
        }"
        :style="{ zoom: zoomLevel }"
      />

      <!-- Loading overlay while a render is in-flight but we already have a
           prior render to keep visible behind it. -->
      <div v-if="status === 'loading' && lastBlob" class="preview-loading-overlay">
        <n-spin size="small" />
      </div>

      <!-- Where the last edit landed. Sticks to the bottom of the scroller so
           it stays in reach however far the user reads; one change is just
           named, several become a stepper. -->
      <div v-if="showChangesPill" class="changes-pill-anchor">
        <n-flex class="changes-pill" align="center" :size="0" :wrap="false" role="status">
          <n-button
            v-if="changeCount > 1"
            size="tiny"
            quaternary
            :aria-label="'Previous change'"
            @click="previousChange"
          >
            <template #icon>
              <n-icon><IconChevronUp /></n-icon>
            </template>
          </n-button>
          <button
            type="button"
            class="changes-pill__label"
            title="Scroll to this change"
            @click="scrollToChange(activeChangeIndex)"
          >
            {{
              changeCount > 1 ? `${activeChangeIndex + 1} of ${changeCount} changes` : '1 change'
            }}
          </button>
          <n-button
            v-if="changeCount > 1"
            size="tiny"
            quaternary
            :aria-label="'Next change'"
            @click="nextChange"
          >
            <template #icon>
              <n-icon><IconChevronDown /></n-icon>
            </template>
          </n-button>
          <n-button
            size="tiny"
            quaternary
            :aria-label="'Hide change highlights'"
            @click="dismissChanges"
          >
            <template #icon>
              <n-icon><IconClose /></n-icon>
            </template>
          </n-button>
        </n-flex>
      </div>
    </div>
  </div>
</template>

<style scoped>
.live-preview-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: v-bind(gSurface);
}

/* Layout (justify/align/gap) handled by NFlex; this keeps the header chrome. */
.preview-header {
  padding: 10px 14px;
  border-bottom: 1px solid v-bind(gBorder);
  background: v-bind(gSoft);
}

.preview-header__title-group {
  min-width: 0;
}

/* font-weight comes from NText `strong`; colour/size kept for exact match. */
.preview-header__title {
  color: v-bind(gInk);
  font-size: v-bind(fsBody);
}

.preview-header__actions {
  flex-shrink: 0;
}

.zoom-control {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 2px;
  border-radius: 6px;
  background: v-bind(gSurface);
  border: 1px solid v-bind(gBorder);
}

.zoom-control__label {
  min-width: 48px;
  padding: 4px 6px;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  font-variant-numeric: tabular-nums;
  color: v-bind(gText);
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1;
}

.zoom-control__label:hover:not(:disabled) {
  background: v-bind(gMuted);
  color: v-bind(gAccentStrong);
}

.zoom-control__label:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.preview-header__divider {
  display: inline-block;
  width: 1px;
  height: 20px;
  background: v-bind(gBorder);
  margin: 0 4px;
}

.preview-body {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  overflow: auto;
  padding: 16px;
  background: v-bind(gMuted);
}

/* Centering handled by NFlex; size constraints kept. */
.preview-empty {
  min-height: 240px;
  padding: 24px;
}

/* Muted colour comes from NText `depth="3"`; size kept for exact match. */
.preview-empty__hint {
  font-size: v-bind(fsRow);
}

.preview-error__detail {
  margin: 0;
  font-family: v-bind(fontMono);
  font-size: v-bind(fsMeta);
  color: v-bind(gAlertText);
  white-space: pre-wrap;
  word-break: break-word;
}

.preview-error__hint {
  margin: 6px 0 0;
  font-size: v-bind(fsMeta);
  color: v-bind(gSubtle);
  line-height: 1.5;
}

.preview-error__hint code {
  background: v-bind(gAlertTint);
  color: v-bind(gAlertText);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: v-bind(fsMeta);
  font-family: v-bind(fontMono);
}

.preview-container {
  min-height: 200px;
}

.preview-container--hidden {
  display: none;
}

/* docx-preview renders pages inside its own `.docx-wrapper` element, which
   ships with a gray background + top/bottom padding applied via inline
   styles. That wrapper's background only spans its own (limited) width, so
   horizontal scrolling reveals the preview-body's different gray — producing
   mismatched bands. Neutralise the wrapper so our `preview-body` is the only
   thing painting a backdrop, then style the page `<section>`s directly.
   `!important` is required because the library sets these as inline styles. */
.preview-container :deep(.docx-wrapper) {
  background: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.preview-container :deep(.docx-wrapper > section.docx) {
  background: v-bind(gSurface);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0 !important;
}

/* Change highlights live on docx-preview's own paragraphs and runs, so they
   have to be reached with :deep(). Only background is painted — no border and
   no edge bar — so highlighting never reflows the page. */
.preview-container :deep(p.gaia-preview-change) {
  background: v-bind(gChangeWash);
  border-radius: 2px;
  transition: background-color 400ms ease;
}

/* The one the stepper is on: the same highlighter, pressed harder. */
.preview-container :deep(p.gaia-preview-change--current) {
  background: v-bind(gChangeCurrent);
  animation: gaia-preview-change-in 1100ms ease-out;
}

/* Where text was taken out there is nothing of the edit left to highlight, so
   the paragraph that closed the gap carries a neutral trace instead: something
   was here. */
.preview-container :deep(p.gaia-preview-change--removed) {
  background: v-bind(gRemovedWash);
}

.preview-container :deep(mark.gaia-preview-mark) {
  background: v-bind(gChangeMark);
  color: inherit;
  border-radius: 3px;
  box-decoration-break: clone;
}

/* Dismissed: the classes stay (the next render replaces the DOM anyway), the
   paint goes. */
.preview-container--changes-hidden :deep(p.gaia-preview-change),
.preview-container--changes-hidden :deep(mark.gaia-preview-mark) {
  background: transparent;
  animation: none;
}

/* Settles on the current paragraph's own wash, not the plain one — otherwise
   the flash ends a shade below where the rule leaves it and reads as a blink. */
@keyframes gaia-preview-change-in {
  from {
    background-color: v-bind(gChangeFlash);
  }

  to {
    background-color: v-bind(gChangeCurrent);
  }
}

/* The stepper floats over the document at the bottom of the scroller. Sticky
   rather than absolute: an absolutely positioned child of a scroll container
   scrolls away with the content. The anchor is inert so the pages under it
   stay clickable; only the pill takes the pointer. */
.changes-pill-anchor {
  position: sticky;
  bottom: 10px;
  z-index: 3;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  pointer-events: none;
}

.changes-pill {
  pointer-events: auto;
  padding: 2px 3px 2px 6px;
  border: 1px solid v-bind(gBorder);
  border-radius: 999px;
  background: v-bind(gSurface);
  box-shadow: v-bind(gPillShadow);
}

.changes-pill__label {
  padding: 2px 6px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  font-variant-numeric: tabular-nums;
  color: v-bind(gInk);
  white-space: nowrap;
  cursor: pointer;
}

.changes-pill__label:hover {
  color: v-bind(gAccentStrong);
}

.preview-loading-overlay {
  position: absolute;
  top: 12px;
  right: 16px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid v-bind(gBorder);
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: v-bind(fsMeta);
  color: v-bind(gText);
  z-index: 2;
}
</style>
